import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SimpleDropdownOption } from '@components/simple-dropdown/simple-dropdown.component';
import { CeuUnit } from 'icreate/common/models/ceu-unit';
import { CommunicationType, communicationTypes } from 'icreate/common/models/fu-unit';
import { Port } from 'icreate/common/models/port';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';
import { ceuValidator } from 'icreate/common/validators/ceu-unit/ceu-unit-validator';

@Component({
    selector: 'app-ceu-unit-dialog',
    templateUrl: './ceu-unit-dialog.component.html',
    styleUrl: './ceu-unit-dialog.component.css',
    standalone: false,
})
export class CeuUnitDialogComponent implements OnInit {
    readonly Line_A_Input = 'lineA';
    readonly Line_B_Input = 'lineB';
    readonly Location_Input = 'location';

    readonly TYPE_OPTIONS: SimpleDropdownOption[] = [
        { value: communicationTypes.serial, string: 'dlg.serial' },
        { value: communicationTypes.lan, string: 'dlg.lan' },
    ];

    location: string;
    lineA: string;
    lineB: string;
    type: CommunicationType;
    ports: Port[];
    validationErrors: ValidationErrorMap = {};

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: {
            ceuUnit: CeuUnit;
            isEditing: boolean;
        },
        private dialogRef: MatDialogRef<CeuUnitDialogComponent>
    ) {}

    ngOnInit(): void {
        if (this.data.ceuUnit.id) {
            this.location = this.data.ceuUnit.location;
            this.type = this.data.ceuUnit.commType;
            this.ports = this.data.ceuUnit.Ports;
        }
    }

    closeDialog() {
        this.dialogRef.close();
    }

    onInputChange(event: { name: string; value: string }) {
        if (event.name == this.Line_A_Input) this.lineA = event.value;
        else if (event.name == this.Line_B_Input) this.lineB = event.value;
        else if (event.name == this.Location_Input) this.location = event.value;

        this.updateValidationErrors();
    }

    save() {
        if (this.isSaveDisabled) return;
        const ceuUnit: CeuUnit = {
            id: this.data.ceuUnit.id,
            index: this.data.ceuUnit.index,
            fcuId: this.data.ceuUnit.fcuId,
            vesselId: this.data.ceuUnit.vesselId,
            Ports: this.ports,
            commType: this.type,
            lineAIp: this.lineA,
            lineBIp: this.lineB,
            location: this.location,
        };
        this.dialogRef.close(ceuUnit);
    }

    handleTypeChanged(event) {
        this.type = event.value;
        if (this.type == communicationTypes.serial) {
            this.ports = this.createPorts(8, communicationTypes.serial);
        } else if (this.type == communicationTypes.lan) {
            this.ports = this.createPorts(4, communicationTypes.lan);
        } else this.ports = [];
        this.updateValidationErrors();
    }

    updateValidationErrors(): void {
        const validationData = {
            location: this.location?.trim(),
            commType: this.type,
            lineAIp: this.lineA,
            lineBIp: this.lineB,
        };

        const errors = getValidationErrors(ceuValidator(), validationData);
        const mappedErrors: { [key: string]: { message: string } } = mapErrors(errors) || {};

        this.validationErrors = Object.keys(mappedErrors).length > 0 ? mappedErrors : null;
    }

    get isSaveDisabled() {
        if (
            this.validationErrors['location'] ||
            this.validationErrors['lineBIp'] ||
            this.validationErrors['lineAIp'] ||
            this.validationErrors['commType']
        )
            return true;
        if (!this.type || !this.lineA || !this.lineB || !this.ports || this.ports.length == 0) return true;
        if (this.data.ceuUnit.id)
            return (
                this.location === this.data.ceuUnit.location &&
                this.type == this.data.ceuUnit.commType &&
                this.lineA == this.data.ceuUnit.lineAIp &&
                this.lineB == this.data.ceuUnit.lineBIp
            );
        return false;
    }

    private createPorts(count: number, type: typeof communicationTypes.serial | typeof communicationTypes.lan): Port[] {
        return Array.from({ length: count }, (_, i) => new Port(i + 1, type));
    }
}
